<template>
  <v-container class="loaded">
    <v-toolbar flat>
      <v-tabs v-model="tab" grow disabled show-arrows>
        <v-tab
          v-for="(item, index) in menuItems"
          :key="index"
          :disabled="index > 1"
        >
          {{ item }}
        </v-tab>
      </v-tabs>
    </v-toolbar>

    <v-tabs-items class="tabs-items" v-model="tab" touchless>
      <v-tab-item key="0">
        <v-row class="flex-col-on-mobile">
          <v-col class="filters">
            <div v-if="this.$vuetify.breakpoint.name !== 'xs'">
              <small>{{ $t("tacShop.view") }}</small>
              <v-select
                v-model="selectedView"
                :items="translatedCalendarViews"
                outlined
                dense
              >
              </v-select>
            </div>
            <strong style="text-transform: uppercase">{{
              $t("tacShop.courses")
            }}</strong>
            <v-btn depressed color="primary" class="rounded-0" block>
              {{ $t("tacShop.fitnessCourses") }}
            </v-btn>
          </v-col>
          <v-col class="mobile-calendar-controls">
            <v-btn text tile :disabled="!previousEnabled" @click="previous"
              >&lt;</v-btn
            >
            <v-btn text tile :disabled="!nextEnabled" @click="next">&gt;</v-btn>
          </v-col>
          <v-col class="calendar-wrapper">
            <v-btn
              class="mt-16 mx-0 hide-on-mobile"
              depressed
              tile
              text
              :disabled="!previousEnabled"
              @click="previous"
              >&lt;</v-btn
            >
            <v-calendar
              ref="calendar"
              v-model="value"
              :locale="locale"
              :weekdays="[1, 2, 3, 4, 5, 6, 0]"
              :type="calendarView"
              :events="events"
              :event-color="getEventColor"
              :event-text-color="getColor"
              :first-interval="0"
              :interval-count="24"
              @change="getEvents"
              @click:event="viewDetails"
              :interval-height="192"
              :event-height="96"
            ></v-calendar>
            <v-btn
              class="mt-16 mx-0 hide-on-mobile"
              depressed
              tile
              text
              :disabled="!nextEnabled"
              @click="next"
              >&gt;</v-btn
            >
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item key="1">
        <v-row id="treatments-and-fitness-container" class="flex-col-on-mobile">
          <v-col class="filters">
            <v-tabs v-model="category" vertical>
              <v-tab
                v-for="(item, index) in activitiesCategories"
                :key="index"
                class="activities-category"
                @click="
                  () => {
                    scrollToItems();
                    readMore = -1;
                  }
                "
              >
                {{
                  (() => {
                    const foundItem = item.name.find(
                      (n) => n.language === locale
                    );
                    return foundItem ? foundItem.value : item.name[0].value;
                  })()
                }}
              </v-tab>
            </v-tabs>
          </v-col>
          <v-col
            id="treatments-and-fitness-items"
            class="pb-4 pt-0 pt-sm-3 pl-1 mt-0 mt-sm-1 mr-0"
          >
            <v-tabs-items v-model="category">
              <v-tab-item
                v-for="(item, index) in activitiesCategories"
                :key="index"
              >
                <v-row>
                  <v-col>
                    <v-card
                      v-for="(activity, index) in getActivities(item.id)"
                      :key="index"
                      class="mx-4 my-4"
                      @click="openActivity(activity)"
                      outlined
                    >
                      <v-card-title>{{ activity.title }}</v-card-title>
                      <v-card-text
                        class="d-flex flex-column flex-sm-row"
                        style="gap: 16px"
                      >
                        <p>
                          {{
                            activity.description.length > 100 &&
                            readMore !== index
                              ? activity.description.substring(0, 100) + "..."
                              : activity.description
                          }}
                          <span
                            v-if="
                              activity.description.length > 100 &&
                              readMore !== index
                            "
                            @click.stop="readMore = index"
                            style="text-decoration: underline; cursor: pointer"
                          >
                            {{ $t("tacShop.readMore") }}
                          </span>
                        </p>
                        <div style="min-width: 100px">
                          <p class="ma-0">
                            {{ $t("tacShop.dialog.duration") }}:
                            {{ activity.durationInMinutes }}
                            {{ $t("tacShop.dialog.minutes") }}
                          </p>
                          <p class="ma-0 font-weight-bold">
                            {{ activity.currency }}
                            {{ activity.price.toFixed(2) }}
                          </p>
                          <p class="ma-0">
                            {{ $t("tacShop.dialog.vatIncluded") }}
                          </p>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs-items>
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item key="2"></v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="bookingDialog.show" max-width="600">
      <v-card>
        <v-card-title class="text-h5">
          {{ bookingDialog.title }}
        </v-card-title>
        <v-card-text>
          {{ bookingDialog.dateTime }}
        </v-card-text>
        <v-card-text>
          {{ bookingDialog.description }}
        </v-card-text>
        <v-card-text style="font-weight: 300">
          <p class="mb-0">
            {{
              $t("tacShop.dialog.available", {
                num: bookingDialog.available,
                max: bookingDialog.maxNumberOfParticipants,
              })
            }}
          </p>
          <p class="mb-0" v-if="bookingDialog.minAge > 0">
            {{ $t("tacShop.dialog.minAge", { age: bookingDialog.minAge }) }}
          </p>
        </v-card-text>

        <v-select
          class="persons-select"
          max-width="10"
          v-model="bookingDialog.persons"
          :items="
            bookingDialog.available
              ? Array.from(
                  { length: bookingDialog.available },
                  (_, index) => index + 1
                )
              : [1]
          "
          :label="$t('tacShop.dialog.numberOfPersons')"
        ></v-select>

        <v-card-text class="text-right pb-0">
          <p class="ma-0">
            {{ $t("tacShop.dialog.duration") }}:
            {{ bookingDialog.durationInMinutes }}
            {{ $t("tacShop.dialog.minutes") }}
          </p>
          <p class="ma-0 font-weight-bold">
            {{ bookingDialog.currency }} {{ bookingDialog.price.toFixed(2) }}
            {{ $t("tacShop.dialog.perPerson") }}
          </p>
          <p class="ma-0">
            {{ $t("tacShop.dialog.vatIncluded") }}
          </p>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="primary darken-1" text @click="bookingDialog.onCancel">
            {{ $t("tacShop.dialog.cancel") }}
          </v-btn>

          <v-btn
            color="primary darken-1"
            text
            @click="bookingDialog.onContinue"
          >
            {{ $t("tacShop.dialog.continue") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmBookingDialog.show" max-width="600">
      <v-card>
        <v-card-title class="text-h5">
          {{ confirmBookingDialog.title }}
        </v-card-title>
        <v-card-text>
          <strong>{{ confirmBookingDialog.itemTitle }} </strong>
          <br />
          {{ confirmBookingDialog.dateTime }}
          <br />
          {{
            $t("tacShop.dialog.numberOfPersons") +
            ": " +
            confirmBookingDialog.persons
          }}
        </v-card-text>

        <v-card-text class="text-right pb-0">
          <p class="ma-0 font-weight-bold">
            {{ confirmBookingDialog.currency }}
            {{
              (
                confirmBookingDialog.price * confirmBookingDialog.persons
              ).toFixed(2)
            }}
            {{ $t("tacShop.dialog.total") }}
          </p>
          <p class="ma-0">
            {{ $t("tacShop.dialog.vatIncluded") }}
          </p>
        </v-card-text>

        <v-card-text class="d-flex align-center">
          <v-checkbox v-model="confirmBookingDialog.termsAndConditions" />
          <span
            v-html="
              $t('tacShop.dialog.termsAndConditions', {
                anchorOpen: `<a href='https://www.aqua-dome.at/wp-content/uploads/2023/03/agb-tac-webshop-03-2023-03-23-${locale}.pdf' target='_blank'>`,
                anchorClose: '</a>',
              })
            "
          ></span>
        </v-card-text>

        <v-card-actions
          class="d-flex justify-space-between flex-column flex-sm-row align-end"
        >
          <v-spacer></v-spacer>

          <v-btn
            color="primary darken-1"
            class="text-left"
            text
            @click="confirmBookingDialog.onBack"
          >
            {{ $t("tacShop.dialog.back") }}
          </v-btn>

          <v-btn
            :disabled="!confirmBookingDialog.termsAndConditions"
            color="primary darken-1"
            text
            @click="confirmBookingDialog.onBook"
          >
            {{ $t("tacShop.dialog.placeBindingOrder") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="bookingSuccessDialog" max-width="350" persistent>
      <v-card>
        <v-card-title class="text-h5">{{
          $t("tacShop.dialog.success")
        }}</v-card-title>
        <v-card-text>{{ $t("tacShop.dialog.successLongText") }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary darken-1"
            text
            @click="onCloseBookingSuccessDialog"
          >
            {{ $t("tacShop.dialog.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="bookingFailedDialog" max-width="350" persistent>
      <v-card>
        <v-card-title class="text-h5">{{
          $t("tacShop.dialog.failed")
        }}</v-card-title>
        <v-card-text>{{ $t("tacShop.dialog.failedLongText") }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary darken-1"
            text
            @click="onCloseBookingFailedDialog"
          >
            {{ $t("tacShop.dialog.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showLoadingDialog" max-width="360" persistent>
      <v-card class="py-12 px-8 d-flex flex-column justify-center align-center">
        <v-progress-circular
          v-if="!status.includes('error')"
          indeterminate
          color="primary"
          size="64"
          class="mb-4"
        ></v-progress-circular>
        <v-icon v-else color="red" size="64" class="mb-4 mt-n4"
          >mdi-cancel</v-icon
        >
        <p v-if="status === 'logging-in'">
          {{ $t("tacShop.loggingIn") }}
        </p>
        <p v-if="status === 'login-error'">
          {{ $t("tacShop.loginError") }}
        </p>
        <p v-if="status === 'loading-courses'">
          {{ $t("tacShop.loadingCalendar") }}
        </p>
        <p v-if="status === 'loading-courses-error'">
          {{ $t("tacShop.loadingCalendarError") }}
        </p>
        <p v-if="status === 'booking-in-progress'">
          {{ $t("tacShop.bookingInProgress") }}
        </p>
        <p v-if="status === 'loading-activities'">
          {{ $t("tacShop.loadingActivities") }}
        </p>
        <p v-if="status === 'loading-activities-error'">
          {{ $t("tacShop.loadingActivitiesError") }}
        </p>
        <p v-if="status === 'loading-available-slots'">
          {{ $t("tacShop.loadingAvailableSlots") }}
        </p>
        <v-card-actions v-if="status.includes('error')" class="mb-n9">
          <v-btn color="primary darken-1" text @click="init()"
            >{{ $t("tacShop.retry") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="activityDialog.show" max-width="600">
      <v-card>
        <v-card-title>
          {{ selectedActivity.title }}
        </v-card-title>
        <v-card-text>
          <strong>
            {{ $t("tacShop.dialog.description") }}
          </strong>
          <br />
          {{ selectedActivity.description }}
        </v-card-text>
        <v-card-text>
          <v-menu
            v-model="activityDialog.datepickeropen"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="activityDialog.date"
                :label="$t('tacShop.dialog.date')"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="activityDialog.date"
              @input="activityDialog.datepickeropen = false"
              color="primary"
            ></v-date-picker>
          </v-menu>
        </v-card-text>
        <v-card-text>
          <v-select
            :items="[
              $t('tacShop.dialog.myself'),
              $t('tacShop.dialog.somebodyElse'),
            ]"
            :label="$t('tacShop.dialog.schedulingFor')"
            v-model="activityDialog.schedulingFor"
          ></v-select>
        </v-card-text>
        <v-card-text
          v-if="
            activityDialog.schedulingFor === $t('tacShop.dialog.somebodyElse')
          "
        >
          <v-text-field
            v-model="activityDialog.notes"
            :label="$t('tacShop.dialog.whoAreYouBookingFor')"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="">
          <v-spacer></v-spacer>
          <div class="d-flex flex-column-reverse align-end flex-sm-row">
            <v-btn
              color="primary darken-1"
              text
              @click="activityDialog.show = false"
            >
              {{ $t("tacShop.dialog.close") }}
            </v-btn>
            <v-btn
              color="primary darken-1"
              text
              @click="checkAvailability()"
              :disabled="
                activityDialog.schedulingFor ===
                  $t('tacShop.dialog.somebodyElse') && !activityDialog.notes
              "
            >
              {{ $t("tacShop.dialog.checkAvailability") }}
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="selectTimeDialog.show" max-width="600">
      <v-card>
        <v-card-title>
          {{ selectedActivity.title }}
        </v-card-title>

        <v-card-text class="d-flex align-center">
          <v-checkbox v-model="selectTimeDialog.termsAndConditions" />
          <span
            v-html="
              $t('tacShop.dialog.termsAndConditions', {
                anchorOpen: `<a href='https://www.aqua-dome.at/wp-content/uploads/2023/03/agb-tac-webshop-03-2023-03-23-${locale}.pdf' target='_blank'>`,
                anchorClose: '</a>',
              })
            "
          ></span>
        </v-card-text>

        <v-card-text v-if="activityDialog.notes">
          <strong>{{ $t("tacShop.dialog.bookingFor") }}: </strong>
          {{ activityDialog.notes }}
        </v-card-text>

        <v-card-text>
          {{ $t("tacShop.dialog.selectTime") }}
        </v-card-text>
        <v-card-text>
          <v-list>
            <v-list-item
              v-for="(timeSlot, index) in selectTimeDialog.timeSlots"
              :key="index"
              style="border-bottom: 1px solid #e0e0e0"
            >
              <v-list-item-content>
                <div
                  class="d-flex justify-space-between flex-column flex-sm-row align-center"
                  style="gap: 16px"
                >
                  <p class="d-flex align-center ma-0">
                    {{
                      hoursAndMinutesStringFromDate(new Date(timeSlot.start))
                    }}
                    -
                    {{ hoursAndMinutesStringFromDate(new Date(timeSlot.end)) }}
                  </p>
                  <div class="d-flex">
                    <p class="d-flex align-center ma-0">
                      <strong>
                        {{
                          selectedActivity.currency +
                          " " +
                          selectedActivity.price.toFixed(2)
                        }}
                      </strong>
                      <small class="ms-1">
                        {{ "(" + $t("tacShop.dialog.vatIncluded") + ")" }}
                      </small>
                    </p>
                  </div>
                  <v-btn
                    :disabled="!selectTimeDialog.termsAndConditions"
                    color="primary darken-1"
                    text
                    @click="handleBookActivity(timeSlot)"
                  >
                    {{ $t("tacShop.dialog.placeBindingOrder") }}
                  </v-btn>
                </div>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary darken-1"
            text
            @click="
              () => {
                selectTimeDialog.show = false;
                activityDialog.show = true;
              }
            "
          >
            {{ $t("tacShop.dialog.back") }}
          </v-btn>
          <v-btn
            color="primary darken-1"
            text
            @click="selectTimeDialog.show = false"
          >
            {{ $t("tacShop.dialog.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="errorDialog.show" max-width="600">
      <v-card>
        <v-card-title>
          {{ errorDialog.title }}
        </v-card-title>
        <v-card-text>
          {{ errorDialog.message }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary darken-1"
            text
            @click="
              () => {
                errorDialog.show = false;
                errorDialog.onClose();
              }
            "
            >{{ $t("tacShop.dialog.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<style scoped>
.container {
  height: 100%;
  width: 100%;
  max-width: 100%;
}

.loaded {
  padding: 0;
  background-color: lightgray;
  color: white;
}

.tabs-items {
  background-color: unset;
}

.filters {
  background-color: white;
  color: black;
  padding: 16px 16px 16px 24px;
  margin-top: 16px;
  margin-bottom: 16px;
  max-width: 20%;
  min-width: 250px; /* this takes priority over max-width */
}

.calendar-wrapper {
  background-color: white;
  color: black;
  margin: 16px 0px 16px 4px;
  display: flex;
}

.v-calendar {
  background-color: none;
  border: none;
  color: black;
  padding-top: 32px;
  padding-bottom: 0px;
  width: 100%;
  max-height: calc(100dvh - 216px);
}

@media (max-width: 960px) {
  .v-calendar {
    max-height: calc(100dvh - 188px);
  }
}

.persons-select {
  max-width: 200px;
  margin-inline-start: 24px;
}

.mobile-calendar-controls {
  display: none;
}

.activities-category {
  display: block;
  width: 200px;
  text-align: left;
  align-content: center;
  font-size: small;
  text-wrap: wrap;
  border-bottom: 1px solid #e0e0e0;
}

@media (max-width: 600px) {
  .activities-category {
    width: 100% !important;
    max-width: calc(100% - 48px);
  }

  .filters {
    max-width: 100%;
    margin: 16px 0 4px 0;
  }

  .v-calendar {
    width: 100%;
    margin: 0px 0px 16px 0px;
    min-height: 500px;
    padding-top: 0;
  }

  .calendar-wrapper {
    margin: 0;
  }

  .hide-on-mobile {
    display: none;
  }

  .flex-col-on-mobile {
    flex-direction: column;
  }

  .mobile-calendar-controls {
    display: flex;
    justify-content: space-between;
    background-color: white;
  }
}
</style>
<style>
.v-calendar-daily_head-day-label > button {
  width: 40px !important;
  height: 40px !important;
}
.v-event-summary {
  min-height: 36px !important;
  text-wrap: wrap !important;
}
</style>
<script>
import Vue from "vue";
import { mapState } from "vuex";
import { DateTime } from "luxon";
export default {
  data: function () {
    return {
      status: "logging-in",
      translatedCalendarViews: [
        Vue.i18n.translate("tacShop.weeklyCalendar"),
        Vue.i18n.translate("tacShop.dailyCalendar"),
      ],
      calendarViews: ["week", "day"],
      selectedView: Vue.i18n.translate("tacShop.weeklyCalendar"),
      value: "",
      events: [],
      bookingDialog: {
        show: false,
        title: "",
        dateTime: "",
        description: "",
        persons: 1,
        available: 1,
        maxNumberOfParticipants: 1,
        minAge: 0,
        price: 0,
        currency: "EUR",
        durationInMinutes: 0,
        onCancel: () => {},
        onContinue: () => {},
      },
      confirmBookingDialog: {
        show: false,
        title: "",
        dateTime: "",
        description: "",
        persons: 1,
        price: 0,
        currency: "EUR",
        onBack: () => {},
        onBook: () => {},
        termsAndConditions: false,
      },
      bookingSuccessDialog: false,
      bookingFailedDialog: false,
      date: new Date(),
      tab: 0,
      menuItems: [
        this.$t("tacShop.courses"),
        this.$t("tacShop.treatmentsAndFitness"),
        // this.$t("tacShop.giftCertificates"),
      ],
      category: 0,
      selectedActivity: {
        id: "",
        title: "",
        description: "",
        price: 0,
        currency: "",
      },
      activityDialog: {
        show: false,
        date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 10),
        datepickeropen: false,
        schedulingFor: this.$t("tacShop.dialog.myself"),
        notes: "",
      },
      selectTimeDialog: {
        show: false,
        timeSlots: [],
        termsAndConditions: false,
      },
      errorDialog: {
        show: false,
        title: "",
        message: "",
        onClose: () => {},
      },
      readMore: -1,
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      this.status = "logging-in";
      try {
        await this.$store.dispatch("tac/postAccountVerify");
      } catch (err) {
        this.status = "login-error";
        return;
      }
      this.status = "loading-courses";
      try {
        await this.$store.dispatch("tac/getAllCourses");
      } catch (err) {
        this.status = "loading-courses-error";
        return;
      }
      this.status = "loading-activities";
      try {
        await this.$store.dispatch("tac/getAllActivities");
      } catch (err) {
        this.status = "loading-activities-error";
        return;
      }
      this.status = "loaded";
      this.scrollToCurrentTime();
      this.preSelectView();
    },
    scrollToCurrentTime() {
      const now = new Date();
      now.setHours(now.getHours() - 1);
      now.setMinutes(55);
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const time = `${hours}:${minutes}`;
      this.$refs.calendar?.scrollToTime(time);
    },
    preSelectView() {
      if (this.$vuetify.breakpoint.name === "xs") {
        this.selectedView = this.translatedCalendarViews[1];
      } else {
        this.selectedView = this.translatedCalendarViews[0];
      }
    },
    getEvents() {
      const events = [];
      for (let i = 0; i < this.allCourses?.courses?.length; i++) {
        const course = this.allCourses.courses[i];
        const startTime = DateTime.fromISO(course.timeSpan.Start, {
          zone: "utc",
        }).toFormat("yyyy-LL-dd HH:mm");
        const endTime = DateTime.fromISO(course.timeSpan.End, {
          zone: "utc",
        }).toFormat("yyyy-LL-dd HH:mm");

        if (course.availableNoOfPersons === 0) {
          continue;
        }

        events.push({
          index: i,
          name:
            course.name.find((n) => n.language === this.locale)?.value ||
            course.name[0].value,
          start: startTime,
          end: endTime,
          color: course.color,
          timed: true,
        });
      }
      this.events = events;
      this.value = this.date;
    },
    getEventColor(event) {
      return event.color;
    },
    getColor(event) {
      const hexToRgb = (hex) => {
        // Remove the hash at the start if it's there
        hex = hex.replace(/^#/, "");

        // Parse the r, g, b values
        let bigint = parseInt(hex, 16);
        let r = (bigint >> 16) & 255;
        let g = (bigint >> 8) & 255;
        let b = bigint & 255;

        return [r, g, b];
      };

      // Helper function to calculate luminance
      function luminance(r, g, b) {
        const a = [r, g, b].map((v) => {
          v /= 255;
          return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
        });
        return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
      }

      // Convert hex color to RGB
      const [r, g, b] = hexToRgb(event.color);

      // Calculate the luminance
      const bgLuminance = luminance(r, g, b);

      // Determine the text color based on luminance
      const textColor = bgLuminance > 0.179 ? "#294754" : "#EEEEEE";

      return textColor;
    },
    viewDetails({ event }) {
      const course = this.allCourses.courses[event.index];
      this.bookingDialog = {
        show: true,
        title:
          course.name.find((n) => n.language === this.locale)?.value ||
          course.name[0].value,
        dateTime: this.formatDateRange(
          course.timeSpan.Start,
          course.timeSpan.End
        ),
        description:
          course.description.find((n) => n.language === this.locale)?.value ||
          course.description[0].value,
        persons: 1,
        available: course.availableNoOfPersons,
        maxNumberOfParticipants: course.maxNoOfPersons,
        minAge: course.minAge,
        price: course.price.value,
        currency: course.price.currencyCode,
        durationInMinutes: this.getDurationInMinutes(course.duration),
        onCancel: () => {
          this.bookingDialog.show = false;
        },
        onContinue: () => {
          this.bookingDialog.show = false;
          this.confirmBookingDialog.persons = this.bookingDialog.persons;
          this.confirmBookingDialog.show = true;
        },
      };
      this.confirmBookingDialog = {
        show: false,
        title: this.$t("tacShop.dialog.placeBindingOrder"),
        itemTitle:
          course.name.find((n) => n.language === this.locale)?.value ||
          course.name[0].value,
        dateTime: this.formatDateRange(
          course.timeSpan.Start,
          course.timeSpan.End
        ),
        description:
          course.description.find((n) => n.language === this.locale)?.value ||
          course.description[0].value,
        persons: this.bookingDialog.persons,
        price: course.price.value,
        currency: course.price.currencyCode,
        onBack: () => {
          this.confirmBookingDialog.show = false;
          this.bookingDialog.show = true;
        },
        onBook: () => {
          this.status = "booking-in-progress";
          this.$store
            .dispatch("tac/bookCourse", {
              courseId: course.classId,
              language: this.locale,
              numberOfPersons: this.bookingDialog.persons,
            })
            .then(() => {})
            .catch(() => {})
            .finally(() => {
              this.confirmBookingDialog.show = false;
            });
        },
        termsAndConditions: false,
      };
    },
    async onCloseBookingSuccessDialog() {
      this.bookingSuccessDialog = false;
      this.status = "loading-courses";
      await this.$store.dispatch("tac/getAllCourses");
      this.status = "loaded";
    },
    async onCloseBookingFailedDialog() {
      this.bookingFailedDialog = false;
      this.status = "loading-courses";
      await this.$store.dispatch("tac/getAllCourses");
      this.status = "loaded";
    },
    formatDateRange(start, end) {
      const daysOfWeek = [
        Vue.i18n.translate("tacShop.formatDateRange.sunday"),
        Vue.i18n.translate("tacShop.formatDateRange.monday"),
        Vue.i18n.translate("tacShop.formatDateRange.tuesday"),
        Vue.i18n.translate("tacShop.formatDateRange.wednesday"),
        Vue.i18n.translate("tacShop.formatDateRange.thursday"),
        Vue.i18n.translate("tacShop.formatDateRange.friday"),
        Vue.i18n.translate("tacShop.formatDateRange.saturday"),
      ];
      const startDate = new Date(start);
      const endDate = new Date(end);
      const startDayOfWeek = daysOfWeek[startDate.getUTCDay()];
      const startDateFormatted = this.formatDate(startDate);

      return `${startDayOfWeek} ${startDateFormatted}, ${this.formatTime(
        startDate
      )} - ${this.formatTime(endDate)}`;
    },
    formatDate(date) {
      const day = date.getUTCDate().toString().padStart(2, "0");
      const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
      const year = date.getUTCFullYear();

      return `${day}.${month}.${year}`;
    },
    formatTime(date) {
      const hours = date.getUTCHours().toString().padStart(2, "0");
      const minutes = date.getUTCMinutes().toString().padStart(2, "0");

      return `${hours}:${minutes}`;
    },
    previous() {
      if (this.selectedView === this.translatedCalendarViews[1]) {
        this.date.setDate(this.date.getDate() - 1);
      } else {
        this.date.setDate(this.date.getDate() - 7);
      }
      this.date = new Date(this.date);
    },
    next() {
      if (this.selectedView === this.translatedCalendarViews[1]) {
        this.date.setDate(this.date.getDate() + 1);
      } else {
        this.date.setDate(this.date.getDate() + 7);
      }
      this.date = new Date(this.date);
    },
    getActivities(id) {
      let activities = this.allActivities.activities.filter(
        (activity) => activity.category === id
      );

      activities = activities.map((activity) => {
        const id = activity.id;
        const title =
          activity.name.find((n) => n.language === this.locale)?.value ||
          activity.name[0].value;
        const description =
          activity.description.find((n) => n.language === this.locale)?.value ||
          activity.description[0].value;
        const price = activity.price.value;
        const currency = activity.price.currencyCode;
        const durationInMinutes = this.getDurationInMinutes(activity.duration);

        return {
          id,
          title,
          description,
          price,
          currency,
          durationInMinutes,
        };
      });

      return activities;
    },
    openActivity(activity) {
      this.selectedActivity = activity;
      this.activityDialog.show = true;
      this.activityDialog.notes = "";
    },
    checkAvailability() {
      this.activityDialog.show = false;
      this.status = "loading-available-slots";
      this.$store
        .dispatch("tac/checkAvailability", {
          activityId: this.selectedActivity.id,
          date: this.activityDialog.date,
          language: this.locale,
        })
        .then((response) => {
          this.selectTimeDialog = {
            show: true,
            timeSlots: response.availableSlots,
            termsAndConditions: false,
          };
        })
        .catch((response) => {
          this.errorDialog = {
            show: true,
            title: this.$t("tacShop.dialog.error"),
            message: response.body,
            onClose: () => (this.activityDialog.show = true),
          };
        })
        .finally(() => {
          this.status = "loaded";
        });
    },
    handleBookActivity(timeSlot) {
      this.selectTimeDialog.show = false;
      this.status = "booking-in-progress";
      this.$store
        .dispatch("tac/bookActivity", {
          activityId: this.selectedActivity.id,
          date: this.activityDialog.date,
          start: this.hoursAndMinutesStringFromDate(new Date(timeSlot.start)),
          end: this.hoursAndMinutesStringFromDate(new Date(timeSlot.end)),
          schedulingFor: this.activityDialog.schedulingFor,
          notes: this.activityDialog.notes,
          language: this.locale,
        })
        .then(() => {})
        .catch(() => {})
        .finally(() => {
          this.status = "loaded";
        });
    },
    hoursAndMinutesStringFromDate(date) {
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
    scrollToItems() {
      const container = document.querySelector(
        "#treatments-and-fitness-container"
      );
      const item = document.querySelector("#treatments-and-fitness-items");
      const offset = item.offsetTop - container.offsetTop;
      window.scrollTo({ top: offset, behavior: "smooth" });
    },
    getDurationInMinutes(duration) {
      const hours = duration.match(/(\d+)H/);
      const minutes = duration.match(/(\d+)M/);

      if (!hours && !minutes) {
        return 0;
      } else if (!hours) {
        return parseInt(minutes[1]);
      }
      return parseInt(hours[1]) * 60 + parseInt(minutes[1]);
    },
  },
  computed: {
    ...mapState({
      allCourses: (state) => state.tac.allCourses,
      statusAllCourses: (state) => state.tac.statusAllCourses,
      allActivities: (state) => state.tac.allActivities,
      activitiesCategories: (state) => state.tac.allActivities.categories,
      statusAllActivities: (state) => state.tac.statusAllActivities,
      statusBookCourse: (state) => state.tac.statusBookCourse,
      statusBookActivity: (state) => state.tac.statusBookActivity,
      locale: ({ i18n }) => i18n.locale,
    }),
    previousEnabled() {
      const dateCopy = new Date(this.date.getTime());
      dateCopy.setHours(0, 0, 0, 0);
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      if (this.selectedView === this.translatedCalendarViews[1]) {
        return dateCopy > now;
      }

      const dayOfWeek = now.getDay();
      const endOfWeek = new Date(now);
      endOfWeek.setDate(now.getDate() + (7 - dayOfWeek));

      return dateCopy > endOfWeek;
    },
    nextEnabled() {
      const dateCopy = new Date(this.date.getTime());
      dateCopy.setHours(0, 0, 0, 0);
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      if (this.selectedView === this.translatedCalendarViews[1]) {
        const currentDay = now.getDay();
        const daysUntilThisSunday = 7 - currentDay;
        const nextWeeksSunday = new Date(now);
        nextWeeksSunday.setDate(now.getDate() + daysUntilThisSunday + 7);

        return dateCopy < nextWeeksSunday;
      }

      const dayOfWeek = now.getDay();
      const endOfWeek = new Date(now);
      endOfWeek.setDate(now.getDate() + (7 - dayOfWeek));

      return dateCopy <= endOfWeek;
    },
    showLoadingDialog() {
      return this.status !== "loaded";
    },
    calendarView() {
      const index = this.translatedCalendarViews.indexOf(this.selectedView);
      return this.calendarViews[index];
    },
  },
  watch: {
    allCourses: {
      handler: function () {
        this.getEvents();
      },
      deep: true,
    },
    statusBookCourse: {
      handler: function () {
        if (this.statusBookCourse === "success") {
          this.status = "loaded";
          this.bookingSuccessDialog = true;
        }
        if (this.statusBookCourse === "failed") {
          this.status = "loaded";
          this.bookingFailedDialog = true;
        }
      },
    },
    statusBookActivity: {
      handler: function () {
        if (this.statusBookActivity === "success") {
          this.status = "loaded";
          this.bookingSuccessDialog = true;
        }
        if (this.statusBookActivity === "failed") {
          this.status = "loaded";
          this.bookingFailedDialog = true;
        }
      },
    },
    date: {
      handler: function () {
        this.$store.dispatch("tac/getAllCourses");
      },
    },
  },
};
</script>
